import { AppleHealthLogo } from '@jouzen/ecom-components/icons/AppleHealthLogo';
import { AppleLogo } from '@jouzen/ecom-components/icons/AppleLogo';
import { GoogleHealthConnectColorLogo } from '@jouzen/ecom-components/icons/GoogleHealthConnectColorLogo';
import { GoogleHealthConnectLogo } from '@jouzen/ecom-components/icons/GoogleHealthConnectLogo';
import { GridItem } from '@jouzen/ecom-components/v3/GridItem';
import { LayoutGrid } from '@jouzen/ecom-components/v3/LayoutGrid';
import { Typography } from '@jouzen/ecom-components/v3/Typography';
import { useGateValue } from '@statsig/react-bindings';
import { useTranslations } from 'next-intl';
import type { MessageKey } from 'types/LocaleMessageKey';
import type { SVGIconComponent } from 'types/svgr';

import Motion from '@/app/components/Motion';
import RichText from '@/app/components/RichText';
import SectionHeading from '@/app/components/SectionHeading';

interface AppsListItemProps {
  readonly Icon: SVGIconComponent;
  readonly name: MessageKey;
}

interface AppsListProps {
  readonly items: AppsListItemProps[];
}

const AppsListItem = ({ Icon, name }: AppsListItemProps) => {
  const t = useTranslations();
  const isBiomarkersEnabled = useGateValue('biomarkers_rollout');

  return (
    <li
      className="inline-flex flex-none items-center gap-x-4"
      data-cy="integrations-apps-list-item"
    >
      {isBiomarkersEnabled ? (
        <Icon className="h-10" />
      ) : (
        <div className="flex size-10 items-center justify-center rounded-full bg-white text-sandstone-500">
          <Icon className="h-4" />
        </div>
      )}
      <Typography className="text-sm" weight="bold">
        {t(name)}
      </Typography>
    </li>
  );
};

const AppsList = ({ items }: AppsListProps) => {
  return (
    <ul
      className="flex flex-wrap gap-x-10 gap-y-6 lg:gap-x-14 xl:gap-x-20"
      data-cy="integrations-apps-list"
    >
      {items.map((item) => (
        <AppsListItem key={item.name} {...item} />
      ))}
    </ul>
  );
};

const IntegrationsApps = () => {
  const t = useTranslations();
  const isBiomarkersEnabled = useGateValue('biomarkers_rollout');

  return (
    <section className="py-20 lg:pt-24 lg:pb-14">
      <LayoutGrid>
        <GridItem colEnd={{ sm: 'main', lg: 13 }} colStart={{ sm: 'main' }}>
          <Motion
            initial={{ opacity: 0 }}
            transition={{ delay: 0.2, duration: 0.75, type: 'tween' }}
            viewport={{ once: true }}
            whileInView={{ opacity: 1 }}
          >
            <Typography
              className="mb-2 text-base tracking-wider uppercase lg:mb-4"
              Element="h2"
              weight="bold"
              color="inherit"
            >
              {t('integrations_apps_section_eyebrow')}
            </Typography>
          </Motion>
          <SectionHeading
            animated
            content="integrations_apps_section_title"
            emAnimationProps={{
              transition: { duration: 1.5, type: 'tween', delay: 0.5 },
            }}
            indentation="pl-20 lg:pl-28"
            multiline
            typographyProps={{
              className: 'leading-tight tracking-tighter mb-0',
              Element: 'h3',
              variant: 'h2',
              color: 'inherit',
            }}
            wrapperAnimationProps={{
              transition: { duration: 1, type: 'tween', delay: 0.35 },
            }}
          />
        </GridItem>
        <GridItem
          className="pt-8 lg:pt-20"
          colEnd={{ sm: 'main' }}
          colStart={{ sm: 'main', lg: 15 }}
        >
          <Motion
            initial={{ opacity: 0 }}
            transition={{ delay: 0.5, duration: 0.75, type: 'tween' }}
            viewport={{ once: true }}
            whileInView={{ opacity: 1 }}
          >
            <Typography className="mb-8 lg:mb-10">
              <RichText messageKey="integrations_apps_section_copy" />
            </Typography>
            <AppsList
              items={[
                {
                  Icon: isBiomarkersEnabled ? AppleHealthLogo : AppleLogo,
                  name: 'integrations_apple_health_kit',
                },
                {
                  Icon: isBiomarkersEnabled
                    ? GoogleHealthConnectColorLogo
                    : GoogleHealthConnectLogo,
                  name: 'integrations_google_health_connect',
                },
              ]}
            />
          </Motion>
        </GridItem>
      </LayoutGrid>
    </section>
  );
};

export default IntegrationsApps;
